import { useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { Section } from '@/components/layout/Section';
import { PageHero } from '@/components/brand/PageHero';
import { Reveal } from '@/components/motion/Reveal';
import { Button } from '@/components/ui/Button';
import { BookCallButton } from '@/components/ui/BookCallButton';
import { TextField, TextArea, SelectField, Honeypot } from '@/components/ui/Field';
import { SocialLinks } from '@/components/ui/SocialLinks';
import { LocationMap } from '@/components/ui/LocationMap';
import { contact } from '@/content/contact';
import { site } from '@/content/site';
import { contactSchema, type ApiResponse } from '@shared/schemas';
import { usePageMeta } from '@/lib/usePageMeta';

const empty = { name: '', email: '', company: '', service: '', message: '', website: '' };

export default function Contact() {
  usePageMeta(
    'Contact Our Calgary Software Team',
    'Talk to a software development team in Calgary about your web app, mobile app or AI automation project. We reply to every enquiry within one working day with honest thoughts on approach and scope.',
    'Contact',
  );

  const [values, setValues] = useState(empty);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const set = (key: keyof typeof empty) => (v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    // Clear a field's error as soon as the user edits it.
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    // Validate with the same schema the server uses, so messages match exactly.
    const parsed = contactSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });
      const data: ApiResponse = await res.json();

      if (!res.ok || !data.ok) {
        if ('errors' in data && data.errors) setErrors(data.errors);
        setFormError(('message' in data && data.message) || 'Something went wrong. Please try again.');
        setStatus('error');
        return;
      }

      setStatus('sent');
      setValues(empty);
    } catch {
      setFormError('Could not reach the server. Please try again, or email us directly.');
      setStatus('error');
    }
  }

  return (
    <>
      <PageHero compact label="Get in touch" headline="Let's build something useful." description="Tell us what you are working on, what needs to change, and where you want to go. You will hear from someone who understands the work." action="Tell us about your project" target="#enquiry" note="Prefer a conversation? You can also book a call below." />

      <Section id="enquiry">
        <div className="grid gap-14 lg:grid-cols-12">
          {/* Form */}
          <div className="lg:col-span-7">
            {status === 'sent' ? (
              <Reveal>
                <div className="rounded-card border border-hairline bg-surface-muted p-10">
                  <h2 className="text-display-md">{contact.form.successHeading}</h2>
                  <p className="text-body-lg mt-4 opacity-75">{contact.form.successBody}</p>
                  <Button
                    variant="secondary"
                    className="mt-8"
                    onClick={() => setStatus('idle')}
                  >
                    Send another message
                  </Button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <h2 className="text-display-md">{contact.form.heading}</h2>
                <form onSubmit={onSubmit} noValidate className="application-panel relative mt-8 space-y-6">
                  <Honeypot value={values.website} onChange={set('website')} />

                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextField
                      name="name"
                      label="Your name"
                      required
                      value={values.name}
                      onChange={set('name')}
                      error={errors.name}
                    />
                    <TextField
                      name="email"
                      label="Email"
                      type="email"
                      required
                      value={values.email}
                      onChange={set('email')}
                      error={errors.email}
                    />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextField
                      name="company"
                      label="Company"
                      value={values.company}
                      onChange={set('company')}
                      error={errors.company}
                    />
                    <SelectField
                      name="service"
                      label="What do you need?"
                      required
                      value={values.service}
                      onChange={set('service')}
                      options={contact.form.serviceOptions}
                      error={errors.service}
                    />
                  </div>

                  <TextArea
                    name="message"
                    label="Tell us about the project"
                    required
                    value={values.message}
                    onChange={set('message')}
                    error={errors.message}
                    placeholder="What are you trying to build or fix? What does success look like?"
                  />

                  {formError && (
                    <p role="alert" className="text-sm text-red-600">
                      {formError}
                    </p>
                  )}

                  <Button type="submit" size="lg" disabled={status === 'sending'}>
                    {status === 'sending' ? 'Sending…' : 'Send message'}
                  </Button>

                  <p className="text-xs leading-relaxed opacity-55">
                    We use your details only to reply to this enquiry. See our{' '}
                    <Link
                      href="/privacy"
                      className="underline underline-offset-2 transition-opacity hover:opacity-70"
                    >
                      privacy policy
                    </Link>
                    .
                  </p>
                </form>
              </Reveal>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-5 lg:pl-8">
            <Reveal delay={0.12}>
              <h2 className="text-eyebrow opacity-65">{contact.details.heading}</h2>

              <a
                href={`mailto:${site.email.general}`}
                className="font-display mt-4 inline-flex min-h-11 items-center text-2xl font-bold transition-colors hover:text-brand"
                style={{ fontStretch: '100%' }}
              >
                {site.email.general}
              </a>

              <p className="mt-6 text-sm leading-relaxed opacity-70">
                {contact.details.responsePromise}
              </p>
              <p className="mt-3 text-sm leading-relaxed opacity-70">{contact.details.note}</p>

              {/* For people who would rather talk than write a brief. The warm
                  tint separates it from the form beside it. */}
              <div className="mt-8 rounded-card border border-accent/25 bg-accent-soft p-6">
                <h3
                  className="font-display text-lg font-bold"
                  style={{ fontStretch: '100%' }}
                >
                  Rather talk it through?
                </h3>
                <p className="mt-2 text-sm leading-relaxed opacity-70">
                  Book a short call and bring the problem — no brief needed.
                </p>
                <BookCallButton variant="primary" size="sm" className="mt-5" />
              </div>

              {/* The office list lives in the map section below, so it is not
                  repeated here. */}
              <h3 className="text-eyebrow mt-12 mb-4 opacity-65">Follow</h3>
              <SocialLinks />
            </Reveal>
          </div>
        </div>
      </Section>

      {/*
        Offices. Two columns rather than a full-width map: the heading and
        copy only fill the left half, so a map spanning the whole shell left a
        large dead area beside the text and dwarfed the section. At lg the map
        sits in that space instead; below lg it stacks underneath.
      */}
      <Section size="sm">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-eyebrow opacity-65">{contact.map.eyebrow}</p>
              <h2 className="text-display-md mt-4">{contact.map.heading}</h2>
              <p className="text-lead mt-5 opacity-75">{contact.map.body}</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <LocationMap locations={site.locations} />
            </Reveal>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="muted">
        <Reveal>
          <p className="text-eyebrow opacity-65">{contact.faq.eyebrow}</p>
          <h2 className="text-display-md mt-4">{contact.faq.heading}</h2>
        </Reveal>

        <div className="mt-12 max-w-[70ch]">
          {contact.faq.items.map((item, i) => (
            <Reveal key={item.q} delay={i * 0.05}>
              <details className="group border-b border-hairline">
                {/*
                  -mx-4 px-4: the hover wash bleeds slightly past the text on
                  both sides so it reads as a row, while the text itself stays
                  aligned with the answer below it.
                */}
                <summary className="hover-summary -mx-4 flex cursor-pointer list-none items-center justify-between gap-6 px-4 py-6 text-left">
                  <span
                    className="font-display text-lg font-bold"
                    style={{ fontStretch: '100%' }}
                  >
                    {item.q}
                  </span>
                  <svg
                    viewBox="0 0 24 24"
                    className="summary-icon h-5 w-5 shrink-0 group-open:rotate-45"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </summary>
                <p className="pb-6 text-sm leading-relaxed opacity-75">{item.a}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </Section>
    </>
  );
}
